import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail, Send, Clock, ToggleLeft, ToggleRight, CheckCircle2, AlertTriangle,
  Loader2, ChevronLeft, Settings, TestTube2, Play, History
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}` } });

export default function EmailDigestSettings({ user }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [logs, setLogs] = useState([]);

  const isAdmin = user?.role?.toLowerCase() === 'admin' || user?.role_name?.toLowerCase()?.includes('admin') || user?.is_super_admin;

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${API}/api/email-digest/config`, getAuth());
      setConfig(data);
    } catch { toast.error('Failed to load config'); }
    setLoading(false);
  };

  const fetchLogs = async () => {
    try {
      const { data } = await axios.get(`${API}/api/email-digest/logs`, getAuth());
      setLogs(data.logs || []);
    } catch {}
  };

  useEffect(() => { fetchConfig(); if (isAdmin) fetchLogs(); }, []);

  const toggleOptOut = async () => {
    try {
      const newVal = !config.user_opted_out;
      await axios.post(`${API}/api/email-digest/opt-out`, { opted_out: newVal }, getAuth());
      setConfig(c => ({ ...c, user_opted_out: newVal }));
      toast.success(newVal ? 'Opted out of daily digest' : 'Opted in to daily digest');
    } catch { toast.error('Failed to update preference'); }
  };

  const toggleEnabled = async () => {
    if (!isAdmin) return;
    try {
      const newVal = !config.enabled;
      await axios.put(`${API}/api/email-digest/config`, { enabled: newVal }, getAuth());
      setConfig(c => ({ ...c, enabled: newVal }));
      toast.success(newVal ? 'Digest enabled for company' : 'Digest disabled');
    } catch { toast.error('Failed'); }
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/email-digest/test`, {}, getAuth());
      toast.success(`Test email sent to ${data.to}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };

  const sendNow = async () => {
    if (!window.confirm('Send digest to all opted-in users now?')) return;
    setSendingAll(true);
    try {
      const { data } = await axios.post(`${API}/api/email-digest/send-now`, {}, getAuth());
      toast.success(`Digest sent to ${data.sent} users${data.errors ? ` (${data.errors} errors)` : ''}`);
      fetchLogs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSendingAll(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-slate-800 text-white px-4 sm:px-6 py-3 flex items-center gap-3 sticky top-0 z-30" data-testid="digest-header">
        <a href="/tasks" className="text-slate-300 hover:text-white"><ChevronLeft className="w-5 h-5" /></a>
        <Mail className="w-5 h-5" />
        <h1 className="text-base sm:text-lg font-semibold">Email Digest Settings</h1>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Status Card */}
        <div className="bg-white rounded-2xl border p-5" data-testid="digest-status-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" /> Daily Digest Schedule
            </h2>
            {config?.smtp_configured ? (
              <span className="text-[11px] bg-green-50 text-green-600 px-2.5 py-1 rounded-full border border-green-200 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> SMTP Ready
              </span>
            ) : (
              <span className="text-[11px] bg-red-50 text-red-600 px-2.5 py-1 rounded-full border border-red-200 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Not Configured
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-xs text-gray-500">Send Time</span>
              <p className="font-medium text-gray-800">9:00 AM IST (Daily)</p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Provider</span>
              <p className="font-medium text-gray-800 capitalize">{config?.provider || 'Gmail'}</p>
            </div>
            <div>
              <span className="text-xs text-gray-500">From</span>
              <p className="font-medium text-gray-800">{config?.smtp_user || 'Not set'}</p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Timezone</span>
              <p className="font-medium text-gray-800">{config?.timezone || 'IST'}</p>
            </div>
          </div>
        </div>

        {/* Digest Content Preview */}
        <div className="bg-white rounded-2xl border p-5">
          <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Mail className="w-4 h-4 text-indigo-500" /> What's Included
          </h2>
          <div className="space-y-2">
            {[
              { label: "Today's Due Tasks", desc: "All tasks due today with priority and assignee", color: 'text-amber-600' },
              { label: 'Overdue Tasks', desc: 'Tasks past their due date still pending', color: 'text-red-600' },
              { label: 'Summary Statistics', desc: 'Total, pending, completed, overdue counts', color: 'text-blue-600' },
              { label: 'Smart Suggestions', desc: 'Auto-created tasks + upcoming lead follow-ups', color: 'text-teal-600' },
              ...(isAdmin ? [{ label: 'Team Performance', desc: 'Completion rates and progress for each member', color: 'text-purple-600' }] : []),
            ].map(item => (
              <div key={item.label} className="flex items-start gap-3 py-2 border-b last:border-0">
                <CheckCircle2 className={`w-4 h-4 ${item.color} shrink-0 mt-0.5`} />
                <div>
                  <span className="text-sm font-medium text-gray-700">{item.label}</span>
                  <p className="text-xs text-gray-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preferences */}
        <div className="bg-white rounded-2xl border p-5 space-y-4">
          <h2 className="font-semibold text-gray-800 flex items-center gap-2">
            <Settings className="w-4 h-4 text-gray-500" /> Preferences
          </h2>

          {/* User opt-out */}
          <div className="flex items-center justify-between py-3 border-b">
            <div>
              <p className="text-sm font-medium text-gray-700">Receive Daily Digest</p>
              <p className="text-xs text-gray-400">Get your task summary every morning at 9 AM</p>
            </div>
            <button onClick={toggleOptOut} className="shrink-0" data-testid="toggle-opt-out">
              {config?.user_opted_out ? (
                <ToggleLeft className="w-10 h-10 text-gray-300" />
              ) : (
                <ToggleRight className="w-10 h-10 text-green-500" />
              )}
            </button>
          </div>

          {/* Admin: enable/disable for company */}
          {isAdmin && (
            <div className="flex items-center justify-between py-3 border-b">
              <div>
                <p className="text-sm font-medium text-gray-700">Company Digest (All Users)</p>
                <p className="text-xs text-gray-400">Enable/disable daily digest for entire company</p>
              </div>
              <button onClick={toggleEnabled} className="shrink-0" data-testid="toggle-company-digest">
                {config?.enabled ? (
                  <ToggleRight className="w-10 h-10 text-green-500" />
                ) : (
                  <ToggleLeft className="w-10 h-10 text-gray-300" />
                )}
              </button>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-white rounded-2xl border p-5 space-y-3">
          <h2 className="font-semibold text-gray-800 flex items-center gap-2">
            <Send className="w-4 h-4 text-blue-500" /> Actions
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={sendTest} disabled={sending} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-700 rounded-xl text-sm font-medium hover:bg-blue-100 border border-blue-200 disabled:opacity-50" data-testid="send-test-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube2 className="w-4 h-4" />}
              Send Test Email to Me
            </button>
            {isAdmin && (
              <button onClick={sendNow} disabled={sendingAll} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 text-white rounded-xl text-sm font-medium hover:bg-slate-700 disabled:opacity-50" data-testid="send-now-btn">
                {sendingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Send to All Users Now
              </button>
            )}
          </div>
        </div>

        {/* Recent Logs (Admin) */}
        {isAdmin && logs.length > 0 && (
          <div className="bg-white rounded-2xl border p-5">
            <h2 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <History className="w-4 h-4 text-gray-500" /> Recent Digest Runs
            </h2>
            <div className="space-y-2">
              {logs.map((log, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b last:border-0 text-xs">
                  <span className="text-gray-600">{log.run_at?.slice(0, 16).replace('T', ' ')}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-green-600 font-medium">{log.total_sent} sent</span>
                    {log.total_errors > 0 && <span className="text-red-500">{log.total_errors} errors</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
